import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { finishFinancials, getPortalWorksheet, patchPortalWorksheet } from "@/features/portal/api";
import { usePortalSessionStore } from "@/features/portal/store/portal-session-store";
import { portalHref } from "@/features/portal/portal-base";
import { AssetsTab } from "./assets-tab";
import { BankingTab } from "./banking-tab";
import { BusinessTab } from "./business/business-tab";
import { FinancialTab } from "./financial-tab";
import { IncomeExpenseTab } from "./incexp-tab";
import { PersonalTab } from "./personal-tab";

/** The raw snake_case worksheet header the portal serves (money in cents). */
export type WorksheetHeader = Record<string, unknown> & { id: string };

const TABS = [
  { slug: "personal", label: "Personal" },
  { slug: "financial", label: "Financial Circumstances" },
  { slug: "assets", label: "Assets" },
  { slug: "banking", label: "Banking & Investments" },
  { slug: "incexp", label: "Income & Expenses" },
] as const;

// Business clients get a single Businesses surface — each entity carries its
// own accounting-period income/expense and drills into detail sub-collections.
const BUSINESS_TABS = [{ slug: "personal", label: "Businesses" }] as const;

type TabSlug = (typeof TABS)[number]["slug"];

function NotAvailable({ title, body }: { title: string; body: string }) {
  return (
    <Card className="p-8 text-center">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-muted-foreground mx-auto mt-2 max-w-md text-sm">{body}</p>
      <Button asChild variant="outline" className="mt-4">
        <Link to={portalHref("/home")}>Back to home</Link>
      </Button>
    </Card>
  );
}

export function PortalFinancials() {
  const params = useParams();
  const navigate = useNavigate();
  const client = usePortalSessionStore((s) => s.client);
  const isBusiness = client?.clientType === "BUSINESS";

  const rawTab = (params.tab as TabSlug) ?? "personal";
  const activeTab: TabSlug = TABS.some((t) => t.slug === rawTab) ? rawTab : "personal";

  const [header, setHeader] = useState<WorksheetHeader | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"ok" | "not-enabled" | "not-available">("ok");
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    let active = true;
    getPortalWorksheet()
      .then((ws) => {
        if (!active) return;
        setHeader(ws as WorksheetHeader);
        setStatus("ok");
      })
      .catch((err) => {
        if (!active) return;
        if (err instanceof ApiError && err.status === 404) setStatus("not-available");
        else if (err instanceof ApiError && err.status === 403) setStatus("not-enabled");
        else setStatus("not-available");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Header autosave: local mirror updated immediately, PATCH fired in background.
  const saveHeader = useCallback((patch: Record<string, unknown>) => {
    setHeader((h) => (h ? { ...h, ...patch } : h));
    patchPortalWorksheet(patch).catch(() => toast.error("Couldn't save — try again."));
  }, []);

  async function confirmFinish() {
    setFinishing(true);
    try {
      await finishFinancials();
      toast.success("Thanks — your financial information has been sent for review.");
      setFinishOpen(false);
      navigate(portalHref("/home"));
    } catch {
      toast.error("Couldn't finish — please try again.");
    } finally {
      setFinishing(false);
    }
  }

  if (loading) return <PageLoader />;
  if (status === "not-available")
    return (
      <NotAvailable
        title="Financial information isn't available yet"
        body="Your practitioner will enable this when it's time to collect your financial details."
      />
    );
  if (status === "not-enabled")
    return (
      <NotAvailable
        title="Financial information isn't enabled"
        body="This section isn't enabled for your account. Please reach out to your practitioner."
      />
    );

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Financial information</h1>
          <p className="text-muted-foreground text-sm">
            Enter your details below — everything saves automatically.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => setFinishOpen(true)}>
          I'm done with financials
        </Button>
      </div>

      {/* Tab strip */}
      <div className="border-border flex flex-wrap gap-1 border-b">
        {(isBusiness ? BUSINESS_TABS : TABS).map((t) => (
          <Button
            key={t.slug}
            asChild
            variant="ghost"
            className={cn(
              "rounded-b-none border-b-2 border-transparent",
              t.slug === activeTab && "border-primary text-primary",
            )}
          >
            <Link to={portalHref(`/financials/${t.slug}`)}>{t.label}</Link>
          </Button>
        ))}
      </div>

      <div>
        {isBusiness ? (
          <BusinessTab />
        ) : (
          <>
            {activeTab === "personal" && <PersonalTab />}
            {activeTab === "financial" && header && (
              <FinancialTab header={header} save={saveHeader} />
            )}
            {activeTab === "assets" && <AssetsTab />}
            {activeTab === "banking" && header && (
              <BankingTab header={header} save={saveHeader} />
            )}
            {activeTab === "incexp" && <IncomeExpenseTab />}
          </>
        )}
      </div>

      <AlertDialog open={finishOpen} onOpenChange={(o) => !finishing && setFinishOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finish financial information?</AlertDialogTitle>
            <AlertDialogDescription>
              This lets your practitioner know you're done entering your financial details. You can
              still be given access again if something needs updating.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={finishing}>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmFinish} disabled={finishing}>
              I'm done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
